import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { resolveTranslationLanguage } from "./getTranslation";
import i18next from "./i18n";
import type { MivoTranslationKey } from "./ITranslation";
import { normalizeStoredLocale, resolveInitialLocale, writeStoredLocale } from "./localeStorage";

/** BCP-47 tags for React Aria + `Intl` / date formatting. */
const BCP47_LOCALE_MAP: Record<MivoTranslationKey, string> = {
  "pt-BR": "pt-BR",
  en: "en-US",
  es: "es-ES",
  de: "de-DE",
};

export function toLocale(language?: string | null): string {
  const key = resolveTranslationLanguage(language ?? i18next.language);
  return BCP47_LOCALE_MAP[key];
}

export async function applyLocale(
  locale: MivoTranslationKey,
  options?: { persistRemote?: () => Promise<void> },
): Promise<void> {
  await i18next.changeLanguage(locale);
  writeStoredLocale(locale);
  if (options?.persistRemote) {
    await options.persistRemote();
  }
}

export function applyLocaleFromPreferred(preferredLocale?: string | null): MivoTranslationKey {
  const locale =
    preferredLocale != null && preferredLocale.trim() !== ""
      ? normalizeStoredLocale(preferredLocale)
      : resolveInitialLocale();
  void i18next.changeLanguage(locale);
  writeStoredLocale(locale);
  return locale;
}

export function useLocale(options?: { onPersist?: (locale: MivoTranslationKey) => Promise<void> }) {
  const { i18n } = useTranslation();
  const locale = resolveTranslationLanguage(i18n.language);

  const setLocale = useCallback(
    async (nextLocale: MivoTranslationKey) => {
      await applyLocale(nextLocale, {
        persistRemote: options?.onPersist ? () => options.onPersist!(nextLocale) : undefined,
      });
    },
    [options?.onPersist],
  );

  return {
    locale,
    setLocale,
    displayLocale: toLocale(locale),
  };
}
